import React, { useEffect, useRef, useState } from "react";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import "./AnimatedStoryImage.css";

const FALLBACK_SRC = "/assets/panda-filled.png";
const REVEAL_MS = 520;
const REVEAL_MS_REDUCED = 320;

/**
 * Magical illustration placeholder + reveal for AI Storybook pages.
 * Does not touch text/narration — purely visual image presentation.
 */
const AnimatedStoryImage = ({
  src,
  alt = "",
  className = "",
  enableAnimation = true,
  objectFit = "cover",
  fallbackSrc = FALLBACK_SRC,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const magical = enableAnimation && !prefersReducedMotion;

  const [phase, setPhase] = useState("placeholder"); // placeholder | revealing | ready
  const [displaySrc, setDisplaySrc] = useState(null);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const revealTimerRef = useRef(null);
  const loadTokenRef = useRef(0);

  useEffect(() => {
    return () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const token = ++loadTokenRef.current;

    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }

    if (!src) {
      setShowPlaceholder(true);
      setPhase("placeholder");
      setDisplaySrc(null);
      return undefined;
    }

    // Soft handoff into magical wait state for the next illustration.
    setShowPlaceholder(true);
    setPhase("placeholder");

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled || token !== loadTokenRef.current) return;
      setDisplaySrc(src);
      setPhase("revealing");
      const duration = magical ? REVEAL_MS : REVEAL_MS_REDUCED;
      revealTimerRef.current = setTimeout(() => {
        if (token !== loadTokenRef.current) return;
        setPhase("ready");
        setShowPlaceholder(false);
        revealTimerRef.current = null;
      }, duration);
    };
    img.onerror = () => {
      if (cancelled || token !== loadTokenRef.current) return;
      setDisplaySrc(fallbackSrc);
      setPhase("revealing");
      const duration = magical ? REVEAL_MS : REVEAL_MS_REDUCED;
      revealTimerRef.current = setTimeout(() => {
        if (token !== loadTokenRef.current) return;
        setPhase("ready");
        setShowPlaceholder(false);
        revealTimerRef.current = null;
      }, duration);
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src, fallbackSrc, magical]);

  const imageClass = [
    "animated-story-image__img",
    phase === "revealing" && (magical ? "animated-story-image__img--reveal" : "animated-story-image__img--reveal-reduced"),
    phase === "ready" && "animated-story-image__img--ready",
    phase === "placeholder" && displaySrc && "animated-story-image__img--fade-out",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`animated-story-image ${magical ? "" : "animated-story-image--reduced"} ${className}`.trim()}
      aria-busy={phase === "placeholder" || phase === "revealing"}
    >
      {showPlaceholder && (
        <div
          className={`animated-story-image__placeholder ${phase === "revealing" || phase === "ready" ? "animated-story-image__placeholder--fade" : ""}`}
          aria-hidden="true"
        >
          <div className="animated-story-image__gradient" />
          <div className="animated-story-image__mist" />
          <div className="animated-story-image__shimmer" />
          <div className="animated-story-image__glow-frame" />
          <span className="animated-story-image__spark animated-story-image__spark--1" />
          <span className="animated-story-image__spark animated-story-image__spark--2" />
          <span className="animated-story-image__spark animated-story-image__spark--3" />
          <span className="animated-story-image__spark animated-story-image__spark--4" />
          <span className="animated-story-image__spark animated-story-image__spark--5" />
          <span className="animated-story-image__star animated-story-image__star--1" />
          <span className="animated-story-image__star animated-story-image__star--2" />
          <span className="animated-story-image__star animated-story-image__star--3" />
        </div>
      )}

      {displaySrc && (
        <img
          key={displaySrc}
          src={displaySrc}
          alt={alt}
          className={imageClass}
          style={{ objectFit }}
          draggable={false}
        />
      )}
    </div>
  );
};

export default AnimatedStoryImage;
